import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Copy, ExternalLink, Loader2, Share2 } from 'lucide-react';
import mangaGenerationService, {
  MangaProject,
  MangaPublicationData,
} from '../../services/api/mangaGenerationService';
import {
  MANGA_BTN_PRIMARY_SM,
  MANGA_BTN_SECONDARY_SM,
  MANGA_DEFAULT_CTA_TEXT,
  MANGA_INPUT,
  MANGA_SETTING_LABEL,
} from '../../constants/manga';

/** パネルが必要とするプロジェクトの断片だけを受け取る */
export type MangaPublicationPanelProject = Pick<
  MangaProject,
  'id' | 'status' | 'is_public' | 'public_url' | 'cta_text' | 'cta_url'
>;

interface MangaPublicationPanelProps {
  project: MangaPublicationPanelProject;
  /** 保存結果を親（表示中プロジェクト・履歴）へ反映させる */
  onUpdated: (projectId: string, data: MangaPublicationData) => void;
}

/**
 * 漫画の公開設定パネル（Tasky 上の公開URL・最終コマの CTA）。
 *
 * MangaGenerationTab から切り出した表示・保存のかたまり。保存後の結果は
 * onUpdated で親へ返し、親が mangaProject / 履歴へマージする。
 *
 * NOTE: 入力欄の同期とメッセージのクリアは必ず別の useEffect に分けること。
 * 1 つにまとめると、保存直後に cta_text / cta_url が変わったタイミングで
 * 同じ effect が走り「CTAを保存しました」を即座に消してしまう（実際に起きた）。
 */
const MangaPublicationPanel: React.FC<MangaPublicationPanelProps> = ({ project, onUpdated }) => {
  const projectId = project.id;
  const savedCtaText = project.cta_text;
  const savedCtaUrl = project.cta_url;

  const [ctaTextInput, setCtaTextInput] = useState<string>(savedCtaText ?? MANGA_DEFAULT_CTA_TEXT);
  const [ctaUrlInput, setCtaUrlInput] = useState<string>(savedCtaUrl ?? '');
  const [isTogglingPublication, setIsTogglingPublication] = useState(false);
  const [isSavingPublicationCta, setIsSavingPublicationCta] = useState(false);
  const [publicationError, setPublicationError] = useState<string | null>(null);
  const [publicationMessage, setPublicationMessage] = useState<string | null>(null);
  const [copiedPublicUrl, setCopiedPublicUrl] = useState(false);
  const publicUrlInputRef = useRef<HTMLInputElement>(null);
  // 「コピーしました」を元に戻すタイマー。アンマウント後に setState しないよう保持する。
  const copiedResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCopiedResetTimer = () => {
    if (copiedResetTimerRef.current !== null) {
      clearTimeout(copiedResetTimerRef.current);
      copiedResetTimerRef.current = null;
    }
  };

  useEffect(() => clearCopiedResetTimer, []);

  // project が差し替わる（履歴クリック・ディープリンク・保存後）たびに
  // 入力欄をサーバー値へ同期する。入力中は project が変わらないので打鍵内容は消えない。
  useEffect(() => {
    setCtaTextInput(savedCtaText ?? MANGA_DEFAULT_CTA_TEXT);
    setCtaUrlInput(savedCtaUrl ?? '');
  }, [projectId, savedCtaText, savedCtaUrl]);

  // 結果表示は別プロジェクトへ切り替わったときだけ消す。
  useEffect(() => {
    setPublicationError(null);
    setPublicationMessage(null);
    setCopiedPublicUrl(false);
    clearCopiedResetTimer();
  }, [projectId]);

  const handleTogglePublic = async (nextIsPublic: boolean) => {
    setPublicationError(null);
    setPublicationMessage(null);
    setIsTogglingPublication(true);

    try {
      const response = await mangaGenerationService.updatePublication(projectId, {
        is_public: nextIsPublic,
      });
      if (response.success && response.data) {
        onUpdated(projectId, response.data);
        setPublicationMessage(nextIsPublic ? '公開しました' : '非公開にしました');
      }
    } catch (err: any) {
      // 422（未完成の漫画を公開しようとした等）は backend のメッセージをそのまま出す
      setPublicationError(err?.response?.data?.message || '公開設定の更新に失敗しました');
    } finally {
      setIsTogglingPublication(false);
    }
  };

  const handleSavePublicationCta = async () => {
    setPublicationError(null);
    setPublicationMessage(null);
    setIsSavingPublicationCta(true);

    try {
      const response = await mangaGenerationService.updatePublication(projectId, {
        cta_text: ctaTextInput.trim() === '' ? null : ctaTextInput.trim(),
        cta_url: ctaUrlInput.trim() === '' ? null : ctaUrlInput.trim(),
      });
      if (response.success && response.data) {
        onUpdated(projectId, response.data);
        setPublicationMessage('CTAを保存しました');
      }
    } catch (err: any) {
      setPublicationError(err?.response?.data?.message || 'CTAの保存に失敗しました');
    } finally {
      setIsSavingPublicationCta(false);
    }
  };

  const handleCopyPublicUrl = async () => {
    const url = project.public_url;
    if (!url) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopiedPublicUrl(true);
        clearCopiedResetTimer();
        copiedResetTimerRef.current = setTimeout(() => {
          copiedResetTimerRef.current = null;
          setCopiedPublicUrl(false);
        }, 2000);
        return;
      }
      throw new Error('clipboard unavailable');
    } catch {
      // クリップボードが使えない環境では選択状態にして手動コピーへ委ねる
      publicUrlInputRef.current?.select();
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-800 [&>svg]:text-indigo-500">
            <Share2 size={18} />
            公開設定
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            公開するとTaskyのURLで誰でも縦スクロールで読めます。CTAボタンは最後のページの下に表示されます。
          </p>
        </div>
        <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={Boolean(project.is_public)}
            onChange={(e) => handleTogglePublic(e.target.checked)}
            disabled={isTogglingPublication}
            className="h-4 w-4 cursor-pointer accent-indigo-500"
          />
          Tasky上で公開する
          {isTogglingPublication && <Loader2 size={14} className="animate-spin text-indigo-500" />}
        </label>
      </div>

      {project.is_public && project.public_url && (
        <div className="mb-4">
          <label className={MANGA_SETTING_LABEL}>公開URL</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={publicUrlInputRef}
              type="text"
              readOnly
              value={project.public_url}
              onFocus={(e) => e.target.select()}
              className="min-w-[240px] flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700"
            />
            <button type="button" onClick={handleCopyPublicUrl} className={MANGA_BTN_SECONDARY_SM}>
              <Copy size={14} />
              {copiedPublicUrl ? 'コピーしました' : 'コピー'}
            </button>
            <a
              href={project.public_url}
              target="_blank"
              rel="noopener noreferrer"
              className={MANGA_BTN_SECONDARY_SM}
            >
              <ExternalLink size={14} />
              開く
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] items-end gap-3 max-[768px]:grid-cols-1">
        <div>
          <label className={MANGA_SETTING_LABEL} htmlFor="manga-cta-text">CTAボタンの文言</label>
          <input
            id="manga-cta-text"
            type="text"
            maxLength={80}
            value={ctaTextInput}
            onChange={(e) => setCtaTextInput(e.target.value)}
            placeholder={MANGA_DEFAULT_CTA_TEXT}
            className={MANGA_INPUT}
          />
        </div>
        <div>
          <label className={MANGA_SETTING_LABEL} htmlFor="manga-cta-url">リンク先URL（LP）</label>
          <input
            id="manga-cta-url"
            type="url"
            value={ctaUrlInput}
            onChange={(e) => setCtaUrlInput(e.target.value)}
            placeholder="https://example.com/lp"
            className={MANGA_INPUT}
          />
        </div>
        <button
          type="button"
          onClick={handleSavePublicationCta}
          disabled={isSavingPublicationCta}
          className={MANGA_BTN_PRIMARY_SM}
        >
          {isSavingPublicationCta ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Check size={14} />
          )}
          保存
        </button>
      </div>

      {publicationMessage && (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <Check size={14} />
          {publicationMessage}
        </p>
      )}
      {publicationError && (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-red-500">
          <AlertCircle size={14} />
          {publicationError}
        </p>
      )}
    </div>
  );
};

export default MangaPublicationPanel;
